"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type SearchActiveContextType = {
  isSearchActive: boolean;
  setSearchActive: (active: boolean) => void;
  /** True while the Typesense search `ReactModal` is open. */
  isSearchModalOpen: boolean;
  /** True while the question detail `ReactModal` is open. */
  isQuestionModalOpen: boolean;
  /**
   * Subscribe/unsubscribe pattern: increments the search-modal counter and
   * returns a function that decrements it. Counter (rather than boolean)
   * keeps multiple instances safe — the desktop and mobile
   * `TypesenseSearchModal` both register without clobbering each other.
   */
  registerSearchModalOpen: () => () => void;
  /** Same pattern, for the question detail modal. */
  registerQuestionModalOpen: () => () => void;
};

const SearchActiveContext = createContext<SearchActiveContextType>({
  isSearchActive: false,
  setSearchActive: () => {},
  isSearchModalOpen: false,
  isQuestionModalOpen: false,
  registerSearchModalOpen: () => () => {},
  registerQuestionModalOpen: () => () => {},
});

export function SearchActiveContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isSearchActive, setSearchActive] = useState(false);
  const [searchModalCount, setSearchModalCount] = useState(0);
  const [questionModalCount, setQuestionModalCount] = useState(0);

  const setSearchActiveStable = useCallback((active: boolean) => {
    setSearchActive(active);
  }, []);

  const registerSearchModalOpen = useCallback(() => {
    setSearchModalCount((c) => c + 1);
    return () => setSearchModalCount((c) => Math.max(0, c - 1));
  }, []);

  const registerQuestionModalOpen = useCallback(() => {
    setQuestionModalCount((c) => c + 1);
    return () => setQuestionModalCount((c) => Math.max(0, c - 1));
  }, []);

  const isSearchModalOpen = searchModalCount > 0;
  const isQuestionModalOpen = questionModalCount > 0;
  const isAnyModalOpen = isSearchModalOpen || isQuestionModalOpen;

  // Drive a body data attribute ourselves rather than relying on react-modal's
  // `bodyOpenClassName`. react-modal's reference counter is managed via
  // class-component `setState` callbacks, which can race with unmount or
  // StrictMode double-invocations and leave its class stuck on <body>. Our
  // counter-based context is the source of truth for "any support-hub modal
  // open", so we toggle the attribute directly.
  useEffect(() => {
    if (!isAnyModalOpen) return;
    document.body.setAttribute("data-support-hub-modal-open", "");
    return () => document.body.removeAttribute("data-support-hub-modal-open");
  }, [isAnyModalOpen]);

  const value = useMemo(
    () => ({
      isSearchActive,
      setSearchActive: setSearchActiveStable,
      isSearchModalOpen,
      isQuestionModalOpen,
      registerSearchModalOpen,
      registerQuestionModalOpen,
    }),
    [
      isSearchActive,
      setSearchActiveStable,
      isSearchModalOpen,
      isQuestionModalOpen,
      registerSearchModalOpen,
      registerQuestionModalOpen,
    ],
  );

  return (
    <SearchActiveContext.Provider value={value}>
      {children}
    </SearchActiveContext.Provider>
  );
}

export const useSearchActive = () => {
  return useContext(SearchActiveContext);
};
